import { useEffect, useState } from "react";
import { Crown, Flame, EnvelopeSimple, Check } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function diff(target, now) {
  const ms = Math.max(0, target - now);
  return {
    days: Math.floor(ms / 86400000),
    hours: Math.floor((ms % 86400000) / 3600000),
    minutes: Math.floor((ms % 3600000) / 60000),
    seconds: Math.floor((ms % 60000) / 1000),
    totalMs: ms,
    done: ms === 0,
  };
}

function Unit({ value, label }) {
  return (
    <div className="flex flex-col items-center min-w-[68px] sm:min-w-[96px]" data-testid={`countdown-${label.toLowerCase()}`}>
      <div className="font-display text-5xl sm:text-7xl text-white leading-none tabular-nums">
        {String(value).padStart(2, "0")}
      </div>
      <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-dim mt-2">{label}</div>
    </div>
  );
}

export default function Countdown() {
  const [config, setConfig] = useState({ target_date: "2026-10-06T00:00:00", signed_total: 100, signed_sold: 0, signed_left: 100 });
  const [t, setT] = useState(() => diff(new Date("2026-10-06T00:00:00").getTime(), Date.now()));
  const [email, setEmail] = useState("");
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    api.get("/launch").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const target = new Date(config.target_date).getTime();
    setT(diff(target, Date.now()));
    const id = setInterval(() => setT(diff(target, Date.now())), 1000);
    return () => clearInterval(id);
  }, [config.target_date]);

  const within30Days = t.totalMs > 0 && t.totalMs <= 30 * 86400000;
  const heading = t.done ? "The King Has Arrived" : (within30Days ? "Final Countdown" : "The Crown Drops");
  const left = config.signed_left ?? Math.max(0, config.signed_total - config.signed_sold);
  const showStock = left > 0 && left < config.signed_total;

  const notify = async (e) => {
    e.preventDefault();
    if (!email) return;
    setJoining(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message);
      setJoined(true);
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <section className="border-y border-white/10 bg-[#0F0F0F] relative overflow-hidden" data-testid="countdown-section">
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 py-12 sm:py-16">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5">
            <div className="section-eyebrow" data-testid="countdown-eyebrow">
              <Flame size={14} weight="fill" /> {within30Days ? "Final Stretch" : "Pre-Launch"}
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase mt-3 leading-[0.95]" data-testid="countdown-heading">
              {heading}<br />
              <span className="text-[#FF3B30]">
                {new Date(config.target_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </span>
            </h2>
            <p className="text-dim mt-4 max-w-md">
              Legacy <span className="text-white">#001</span> drops in the SCU. {showStock
                ? <>Only <span className="text-[#FFD700] font-display tracking-wider" data-testid="signed-left">{left}</span> of {config.signed_total} signed variants left.</>
                : <>The first {config.signed_total} buyers get the foil-stamped signed variant — only available during launch week.</>}
            </p>

            <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 border border-[#FFD700]/40 text-[#FFD700] text-[11px] uppercase tracking-[0.25em]" data-testid="countdown-badge">
              <Crown size={12} weight="fill" />
              {showStock ? `${left} / ${config.signed_total} Signed Variants Left` : `First ${config.signed_total} — Signed Variant`}
            </div>

            {/* Notify-me inline form */}
            {!joined ? (
              <form onSubmit={notify} className="mt-6 flex gap-2 max-w-md" data-testid="notify-form">
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@savage.com"
                  className="flex-1 bg-black border border-white/15 px-4 py-2.5 focus:border-[#FF3B30] outline-none text-sm"
                  data-testid="notify-email"
                />
                <button type="submit" disabled={joining} className="btn-red text-xs inline-flex items-center gap-2" data-testid="notify-submit">
                  <EnvelopeSimple size={14} weight="bold" /> {joining ? "..." : "Notify Me"}
                </button>
              </form>
            ) : (
              <div className="mt-6 inline-flex items-center gap-2 text-[#FFD700]" data-testid="notify-success">
                <Check size={16} weight="bold" /> You&apos;re on the King&apos;s list.
              </div>
            )}
          </div>

          <div className="lg:col-span-7">
            <div className="flex justify-center lg:justify-end items-start gap-3 sm:gap-6 flex-wrap" data-testid="countdown-clock">
              <Unit value={t.days} label="Days" />
              <div className="font-display text-5xl sm:text-7xl text-[#FF3B30] leading-none">:</div>
              <Unit value={t.hours} label="Hours" />
              <div className="font-display text-5xl sm:text-7xl text-[#FF3B30] leading-none">:</div>
              <Unit value={t.minutes} label="Minutes" />
              <div className="font-display text-5xl sm:text-7xl text-[#FF3B30] leading-none">:</div>
              <Unit value={t.seconds} label="Seconds" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
